<script setup>
  import {computed, nextTick, onMounted, onUnmounted, ref, unref, useSlots, watch} from "vue";

  import ComponentIcon from "@components/Icons/Icon.vue";
  import {computePosition, flip} from "@floating-ui/vue";
  import {MedSpinner} from "@pharma4u/medicheck-components";
  import {debounce, isString} from "es-toolkit";

  defineOptions({
    name: "ComponentAutocomplete",
  });

  const slots = useSlots();

  const props = defineProps({
    label: {
      type: String,
      default: "",
    },
    modelValue: {
      type: [String, Object],
      default: "",
    },
    items: {
      type: Array,
      default: () => [],
    },
    fetchMethod: {
      type: Function,
      default: null,
    },
    fetchPaginationMethod: {
      type: Function,
      default: null,
    },
    paginationLinks: {
      type: Object,
      default: () => ({}),
    },
    filterMethod: {
      type: Function,
      default: null,
    },
    keyName: {
      type: String,
      default: "",
    },
    minSearchTermLength: {
      type: Number,
      default: 3,
    },
    validation: {
      type: String,
      default: "",
    },
    validationOnEmptyField: {
      type: Boolean,
      default: false,
    },
    helperText: {
      type: String,
      default: "",
    },
    tabindex: {
      type: [String, Number],
      default: 0,
    },
    message: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(["opened", "closed", "cleared", "selected", "update:modelValue", "hasResults"]);

  const refInput = ref(null);
  const refResultList = ref(null);
  const refAutocomplete = ref(null);
  const refTriggerElement = ref(null);
  const refFloatingElement = ref(null);

  const fieldId = ref(crypto.randomUUID());

  // data
  const searchTerm = ref("");
  const selectedItem = ref(null);
  const results = ref([]);
  const isLoading = ref(false);
  const isDirty = ref(false);
  const showDropdown = ref(false);
  const isInaccurate = ref(false);
  const oldItem = ref("");

  // computed
  const hasItems = computed(() => props.items && props.items.length > 0);
  const hasFilteredItems = computed(() => filteredResults.value.length > 0);
  const hasScopedItemSlot = computed(() => !!slots["autocomplete-item"]);
  const hasScopedEmptySlot = computed(() => !!slots["autocomplete-empty"]);
  const hasScopedRecommendationSlot = computed(
    () => !!slots["autocomplete-recommendation"] && slots["autocomplete-recommendation"]()[0]?.children !== "v-if",
  );
  const hasScopedPrependSlot = computed(() => !!slots["autocomplete-prepend"]);
  const showEmptySlot = computed(
    () =>
      searchTerm.value !== "" &&
      !isLoading.value &&
      !hasFilteredItems.value &&
      !selectedItem.value &&
      hasScopedEmptySlot.value &&
      showDropdown.value,
  );
  const showRecommendationSlot = computed(
    () => showDropdown.value && searchTerm.value === "" && hasScopedRecommendationSlot.value,
  );
  const showPrependSlot = computed(() => showDropdown.value && searchTerm.value !== "" && hasScopedPrependSlot.value);
  const isInvalid = computed(() =>
    typeof props.validation !== "string"
      ? false
      : props.validation.length > 0 && (props.validationOnEmptyField || searchTerm.value.length > 0),
  );
  const hasSearchTerm = computed(
    () =>
      !!searchTerm.value &&
      searchTerm.value.trim().length >= props.minSearchTermLength + (searchTerm.value.trim().charAt(0) === "*" ? 1 : 0),
  );

  // watch all conditions which cause the "popover" refFloatingElement to show
  watch(
    [isLoading, showEmptySlot, showRecommendationSlot, showDropdown],
    ([newIsLoading, newShowEmptySlot, newShowRecommendationSlot, newShowDropdown]) => {
      if (newIsLoading || newShowEmptySlot || newShowRecommendationSlot || newShowDropdown) {
        refFloatingElement.value.showPopover();
        nextTick(() => {
          calculate();
        });
        window.addEventListener("resize", calculate);
      } else {
        window.removeEventListener("resize", calculate);
      }
    },
  );

  onMounted(() => {
    globalThis.addEventListener("click", onClickOutside);
  });

  onUnmounted(() => {
    globalThis.removeEventListener("click", onClickOutside);
    window.removeEventListener("resize", calculate);
    calculate.cancel();
  });

  const calculate = debounce(() => {
    // note: both refs must be real DOM Elements before calling computePosition.
    // unref() alone does not protect against null / already-unmounted elements,
    // which is the root cause of "parameter 1 is not of type 'Element'".
    const triggerElement = unref(refTriggerElement);
    const floatingElement = unref(refFloatingElement);

    if (!(triggerElement instanceof Element) || !(floatingElement instanceof Element)) {
      return;
    }

    try {
      computePosition(triggerElement, floatingElement, {
        placement: "bottom-start", // << preferred placement
        middleware: [flip()],
      }).then(({x, y}) => {
        // Re-check after the async call in case the element was removed in the meantime
        if (refFloatingElement.value instanceof Element) {
          Object.assign(refFloatingElement.value.style, {
            left: `${x}px`,
            top: `${y}px`,
          });
        }
      });
    } catch (error) {
      console.error("Autocomplete computePosition error:", error);
    }
  }, 400);

  const canFetch = computed(() => hasSearchTerm.value && typeof props.fetchMethod === "function");
  const canFilter = computed(() => results.value.length > 0 && typeof props.filterMethod === "function");
  const selectedItemTerm = computed(() => {
    if (selectedItem.value && typeof selectedItem.value === "object" && props.keyName.length > 0) {
      return selectedItem[props.keyName];
    } else if (isString(selectedItem.value)) {
      return selectedItem.value;
    }
    return "";
  });
  const filteredResults = computed(() => (canFilter.value ? props.filterMethod(results.value) : results.value));

  watch(
    () => showDropdown.value,
    (show) => {
      if (show && filteredResults.value.length > 0) {
        emit("opened");
      } else {
        emit("closed");
      }
    },
  );

  watch(
    () => filteredResults.value,
    (results) => {
      emit("hasResults", results.length);
    },
  );

  watch(
    () => props.modelValue,
    (newValue) => {
      if (typeof newValue !== "object") {
        searchTerm.value = props.modelValue || "";
      }
    },
  );

  // Setup
  searchTerm.value = props.modelValue || "";
  selectedItem.value = props.modelValue || null;
  oldItem.value = props.modelValue || null;

  // Methods
  const executeFetchHandler = debounce(() => {
    if (hasSearchTerm.value === false) {
      results.value = [];
      isLoading.value = false;
      return;
    }

    isInaccurate.value = false;
    isLoading.value = true;

    props
      .fetchMethod(searchTerm.value)
      .then((res) => {
        if (hasSearchTerm.value === false) {
          isLoading.value = false;
          return;
        }

        if (res === false) {
          // a cancel token ended the former
          // request and we keep waiting
          isLoading.value = true;
        }

        if (typeof results.value == "object") {
          isLoading.value = false;
          results.value = res;

          // A scanned "PZN" value starts with a '-'.
          const match = searchTerm.value.match(/^-(\d{7,8})/i);
          if (match && match[1] && res.length === 1) {
            selectItem(res[0]);
          }

          // when a search returns more than 500 results
          // we show a hint to type more chars
          isInaccurate.value = res.length >= 500;
        }
      })
      .catch(() => {
        // TODO: this catch wont get called at the moment
        // TODO: and if, we cannot set the value of a prop here...
        isLoading.value = false;
        reset();
      });
  }, 1000);

  const handleFocusIn = ($event) => {
    $event.target.select();
    showDropdown.value = true;
  };

  const toggleDropdown = (timeout = 0) => {
    setTimeout(() => {
      // only toggle dropdown after timeout if
      // dropdown is already shown. so it's
      // not a toggle, it's a conditional
      // closeDropdown after a timeout.
      if (showDropdown.value) {
        showDropdown.value = false;
      }
    }, timeout);
  };

  const onClickOutside = ($event) => {
    if ($event !== "undefined" && refAutocomplete.value && !refAutocomplete.value.contains($event.target)) {
      showDropdown.value = false;
      // handleBlur();
    }
  };

  const selectItem = (item) => {
    if (oldItem.value !== null && oldItem.value.length > 0) {
      emit("cleared", oldItem.value);
      reset();
    }

    isDirty.value = false;
    selectedItem.value = item;
    $_setSearchTerm(selectedItem.value);
    toggleDropdown();
    results.value = [];
    emit("selected", selectedItem.value);
  };

  const searchTermChanged = ($event) => {
    if (!$event.target.value) {
      if ($event.target.value === "") {
        clearField();
      }

      // MED-4490: reset searchTerm if value is empty, otherwise first letter
      searchTerm.value = "";
      emit("update:modelValue", searchTerm.value);
      return;
    }

    if (selectedItemTerm.value === $event.target.value) {
      return;
    }

    let localSearchTerm = $event.target.value;
    if (localSearchTerm.charAt(0) === "#") {
      localSearchTerm = localSearchTerm.slice(1);
    }

    searchTerm.value = localSearchTerm;

    if (hasItems.value) {
      $_filterItems();
    } else if (canFetch.value) {
      results.value = [];
      executeFetchHandler();
    }

    emit("update:modelValue", searchTerm.value);
  };

  const $_filterItems = () => {
    if (!!searchTerm.value === false) {
      return;
    }

    results.value = props.items.filter((item) => {
      if (typeof item === "object" && props.keyName.length > 0) {
        return item[props.keyName].toLowerCase().includes(searchTerm.value.toLowerCase());
      } else if (isString(item)) {
        return item.toLowerCase().includes(searchTerm.value.toLowerCase());
      } else {
        return true;
      }
    });
  };

  const $_setSearchTerm = (item) => {
    if (!item) {
      return;
    }

    if (typeof item === "object" && props.keyName.length > 0) {
      searchTerm.value = item[props.keyName];
    } else if (isString(item)) {
      searchTerm.value = item;
    }
  };

  const reset = () => {
    selectedItem.value = null;
    results.value = [];
    searchTerm.value = "";
  };

  const clearField = () => {
    emit("cleared", oldItem.value);
    reset();
  };

  /** @param {KeyboardEvent} event*/
  const selectPrevious = (event) => {
    event.preventDefault();

    const index = [...refResultList.value.children].indexOf(event.target);

    if (index > 0) {
      refResultList.value.children.item(index - 1).focus();
    } else if (index === 0) {
      refResultList.value.children.item(refResultList.value.children.length - 1).focus();
    }
  };

  /** @param {KeyboardEvent} event*/
  const selectNext = (event) => {
    event.preventDefault();

    const index = [...refResultList.value.children].indexOf(event.target);

    //
    if (index === -1 && filteredResults.value.length > 0 && refResultList.value.children.item(0)) {
      refResultList.value.children.item(0).focus();

      // Still objects in list, select the next one
    } else if (index < refResultList.value.children.length - 1 && refResultList.value.children.item(index + 1)) {
      refResultList.value.children.item(index + 1).focus();

      // Last element, set focus on the first one
    } else if (index === refResultList.value.children.length - 1 && refResultList.value.children.item(0)) {
      refResultList.value.children.item(0).focus();
    }
  };

  const focus = () => {
    refInput.value?.focus();
  };

  defineExpose({
    clearField,
    reset,
    selectItem,
    focus,
    onClickOutside,
  });
</script>

<template>
  <div ref="refAutocomplete" class="relative" :data-test="getTestId('autocomplete-container')">
    <!-- ESLint cannot verify dynamic :for/:id binding at static analysis time -->
    <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
    <label
      :class="{'text-mcred': isInvalid, 'text-gray-500': !isInvalid}"
      class="origin-0 -z-1 text-sm whitespace-nowrap duration-300"
      :for="fieldId"
      :data-test="getTestId('autocomplete-label')"
    >
      {{ label }}
    </label>

    <div ref="refTriggerElement" class="flex">
      <input
        :id="fieldId"
        ref="refInput"
        autocomplete="off"
        :value="searchTerm"
        type="text"
        :tabindex="tabindex"
        class="h-9 px-2"
        :placeholder="placeholder"
        :disabled="disabled"
        :data-test="getTestId('autocomplete-input')"
        @input="searchTermChanged"
        @focusin="handleFocusIn"
        @keydown.down="selectNext"
        @keydown.tab="onClickOutside"
      />

      <ComponentIcon
        v-if="!disabled && clearable"
        :clickable="true"
        class="mt-2.5 -ml-4 w-4 text-base text-gray-500"
        :test-id="getTestId('autocomplete-clear')"
        @click="clearField"
      >
        clear
      </ComponentIcon>
    </div>

    <div ref="refFloatingElement" class="absolute top-0 left-0 m-0 w-max p-0 shadow-lg" popover="manual">
      <div
        v-if="isLoading"
        class="z-10 flex w-96 justify-center rounded-md bg-white p-3 focus:outline-none"
        :data-test="getTestId('autocomplete-loading')"
      >
        <MedSpinner size="lg" :data-test="getTestId('autocomplete-spinner')" />
      </div>

      <div
        v-else-if="showEmptySlot"
        class="z-10 min-h-8 max-w-96 rounded-md bg-white focus:outline-none"
        :data-test="getTestId('autocomplete-empty-state')"
      >
        <slot v-if="hasScopedPrependSlot && results.length > 0" name="autocomplete-prepend" :items="results" />
        <slot name="autocomplete-empty" :term="searchTerm" />
      </div>

      <div
        v-else-if="showRecommendationSlot"
        role="button"
        tabindex="0"
        class="z-10 flex max-h-60 min-h-8 max-w-96 rounded-md bg-white focus:outline-none"
        :data-test="getTestId('autocomplete-recommendations')"
        @click="toggleDropdown(300)"
        @keydown.enter="toggleDropdown(300)"
        @keydown.space.prevent="toggleDropdown(300)"
      >
        <slot name="autocomplete-recommendation" :select-item="selectItem" :term="searchTerm" />
      </div>

      <div
        v-else-if="showDropdown"
        class="z-10 max-w-96 rounded-md bg-white"
        :data-test="getTestId('autocomplete-dropdown')"
      >
        <div v-if="showPrependSlot">
          <slot name="autocomplete-prepend" :items="results" />
        </div>

        <ul
          ref="refResultList"
          tabindex="-1"
          class="max-h-60 space-y-2 overflow-x-hidden overflow-y-auto text-ellipsis focus:outline-none"
          role="listbox"
          :data-test="getTestId('autocomplete-result-list')"
        >
          <li
            v-if="isInaccurate"
            class="px-3 py-1 text-xs text-gray-500"
            :data-test="getTestId('autocomplete-inaccurate-message')"
          >
            Geben Sie mehr Buchstaben für exakte Suchtreffer ein.
          </li>

          <li
            v-for="(item, index) in filteredResults"
            :id="`refResultList${index}`"
            :key="index"
            class="relative z-10 cursor-pointer border-1 border-white px-3 py-1 text-gray-900 hover:bg-gray-100 focus:border-1 focus:border-mcred focus-visible:outline-none"
            role="option"
            tabindex="0"
            :data-test="getTestId('autocomplete-item-' + index)"
            :aria-selected="selectedItem === item"
            @click="selectItem(item)"
            @keydown.enter="selectItem(item)"
            @keydown.up="selectPrevious"
            @keydown.down="selectNext"
            @keydown.tab="onClickOutside"
          >
            <slot v-if="hasScopedItemSlot" name="autocomplete-item" :item="item" :term="searchTerm" />
            <template v-else>{{ item }}</template>
          </li>
        </ul>
      </div>
    </div>

    <span v-if="isInvalid" class="text-xs text-mcred" :data-test="getTestId('autocomplete-error-message')">
      {{ validation }}
    </span>

    <span class="text-xs text-gray-500" :data-test="getTestId('autocomplete-helper-text')">{{ helperText }}</span>
  </div>
</template>
